import BankAccountFormatDropdown from "components/dropdowns/reactselect/BankAccountFormatDropdown";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import React, { useState } from "react";
import { getBankAccountFormat } from "utilities";
import AbaRoutingNumberInput from "./edit/AbaRoutingNumberInput";
import BsbCodeInput from "./edit/BsbCodeInput";
import IbanInput from "./edit/IbanInput";
import InstitutionNumberInput from "./edit/InstitutionNumberInput";
import SortCodeInput from "./edit/SortCodeInput";
import SwiftCodeInput from "./edit/SwiftCodeInput";
import TransitNumberInput from "./edit/TransitNumberInput";
import { BankAccountInfo, getValueIfVisibleOrDefault } from "./models";
import { LinkedBookkeepingsBankAccountPanel } from "components/linkedBookkeepings/LinkedBookkeepingsBankAccount";

interface BankAccountDetailSectionProps {
  bankAccount: server.dto.BankAccount
}

export const BankAccountDetailSection = (props: BankAccountDetailSectionProps) => {
  const [bankAccount, setBankAccount] = useState<BankAccountInfo>({
    title: props.bankAccount.title ?? "",
    beneficiaryName: props.bankAccount.beneficiaryName ?? "",
    format: getBankAccountFormat(props.bankAccount.format),
    abaRoutingNumber: props.bankAccount.abaRoutingNumber ?? "",
    accountNumber: props.bankAccount.accountNumber ?? "",
    bsbCode: props.bankAccount.bsbCode ?? "",
    iban: props.bankAccount.ibanNumber ?? "",
    swiftCode: props.bankAccount.swiftCode ?? "",
    sortCode: props.bankAccount.sortCode ?? "",
    bankAddress: props.bankAccount.bankAddress ?? "",
    branchName: props.bankAccount.branchName ?? "",
    branchCode: props.bankAccount.branchCode ?? "",
    institutionNumber: props.bankAccount.institutionNumber ?? "",
    transitNumber: props.bankAccount.transitNumber ?? "",
    memo: props.bankAccount.memo ?? "",
  });

  const onValueChanged = (key: string, value: any) => {
    if (key === "format") {
      setBankAccount({ ...bankAccount, format: value });
    } else if (key === "title") {
      setBankAccount({ ...bankAccount, title: value });
    } else if (key === "beneficiaryName") {
      setBankAccount({ ...bankAccount, beneficiaryName: value });
    } else if (key === "routingNumber") {
      setBankAccount({ ...bankAccount, abaRoutingNumber: value });
    } else if (key === "accountNumber") {
      setBankAccount({ ...bankAccount, accountNumber: value });
    } else if (key === "bsbCode") {
      setBankAccount({ ...bankAccount, bsbCode: value });
    } else if (key === "ibanNumber") {
      setBankAccount({ ...bankAccount, iban: value });
    } else if (key === "sortCode") {
      setBankAccount({ ...bankAccount, sortCode: value });
    } else if (key === "swiftCode") {
      setBankAccount({ ...bankAccount, swiftCode: value });
    } else if (key === "bankAddress") {
      setBankAccount({ ...bankAccount, bankAddress: value });
    } else if (key === "branchName") {
      setBankAccount({ ...bankAccount, branchName: value });
    } else if (key === "branchCode") {
      setBankAccount({ ...bankAccount, branchCode: value });
    } else if (key === "institutionNumber") {
      setBankAccount({ ...bankAccount, institutionNumber: value });
    } else if (key === "transitNumber") {
      setBankAccount({ ...bankAccount, transitNumber: value });
    } else if (key === "memo") {
      setBankAccount({ ...bankAccount, memo: value });
    }
  }

  const getUpdateBankAccountInstruction = (bankAccountInfo: BankAccountInfo): server.dto.UpdateBankAccountInstruction => {
    return {
      bankAccountId: props.bankAccount.bankAccountId,
      format: bankAccountInfo.format.value,
      title: bankAccountInfo.title,
      beneficiaryName: bankAccountInfo.beneficiaryName,
      accountNumber: getValueIfVisibleOrDefault(bankAccountInfo.accountNumber, bankAccountInfo.format.displayAccountNumberInput),
      ibanNumber: getValueIfVisibleOrDefault(bankAccountInfo.iban, bankAccountInfo.format.displayIbanNumberInput),
      bankAddress: bankAccountInfo.bankAddress,
      branchName: bankAccountInfo.branchName,
      branchCode: bankAccountInfo.branchCode,
      swiftCode: getValueIfVisibleOrDefault(bankAccountInfo.swiftCode, bankAccountInfo.format.displaySwiftCodeInput),
      sortCode: getValueIfVisibleOrDefault(bankAccountInfo.sortCode, bankAccountInfo.format.displaySortCodeInput),
      bsbCode: getValueIfVisibleOrDefault(bankAccountInfo.bsbCode, bankAccountInfo.format.displayBsbCodeInput),
      abaRoutingNumber: getValueIfVisibleOrDefault(bankAccountInfo.abaRoutingNumber, bankAccountInfo.format.displayAbaRoutingNumberInput),
      institutionNumber: getValueIfVisibleOrDefault(bankAccountInfo.institutionNumber, bankAccountInfo.format.displayInstitutionNumberInput),
      transitNumber: getValueIfVisibleOrDefault(bankAccountInfo.transitNumber, bankAccountInfo.format.displayTransitNumberInput),
      memo: bankAccountInfo.memo,
    }
  };

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      key: "bankAccountDetails",
      type: "regular",
      title: RESX.BankAccount.resxBankAccount,
      fields: [{
        key: "title",
        label: RESX.GeneralLabels.InternalTitle,
        render: () => <div>{bankAccount.title}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.bankAccount.maxTitleLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.title,
          submitChange: (title: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            title,
          }),
        },
      }, {
        key: "format",
        label: RESX.BankAccount.Format,
        render: () => <div>{bankAccount.format.text}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.BankAccountFormat>) => <BankAccountFormatDropdown
            value={value}
            onChange={(v) => onChange(v, true)}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bankAccount.format,
          submitChange: (format: server.dto.BankAccountFormat) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            format: format.value,
          }),
        },
      }, {
        key: "beneficiaryName",
        label: RESX.BankAccount.BeneficiaryName,
        render: () => <div>{bankAccount.beneficiaryName}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="beneficiaryName"
            focusOnMount
            maxLength={serverReference.constants.bankAccount.maxBeneficiaryNameLength}
            value={value}
            onChange={(name) => { onChange(name); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.beneficiaryName,
          submitChange: (beneficiaryName: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            beneficiaryName,
          }),
        },
      }, {
        key: "routingNumber",
        label: RESX.BankAccount.RoutingNumber,
        render: () => <div>{bankAccount.abaRoutingNumber}</div>,
        hidden: !bankAccount.format.displayAbaRoutingNumberInput,
        isEmpty: !bankAccount.abaRoutingNumber,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <AbaRoutingNumberInput
            focusOnMount
            validatorKey="abaRoutingNumber"
            value={value}
            onChanged={(nr) => { onChange(nr); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.abaRoutingNumber,
          submitChange: (abaRoutingNumber: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            abaRoutingNumber,
          }),
        }
      }, {
        key: "institutionNumber",
        label: RESX.BankAccount.InstitutionNumber,
        render: () => <div>{bankAccount.institutionNumber}</div>,
        hidden: !bankAccount.format.displayInstitutionNumberInput,
        isEmpty: !bankAccount.institutionNumber,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <InstitutionNumberInput
            focusOnMount
            validatorKey="institutionNumber"
            value={value}
            onChanged={(nr) => { onChange(nr); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.institutionNumber,
          submitChange: (institutionNumber: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            institutionNumber,
          }),
        }
      }, {
        key: "accountNumber",
        label: RESX.BankAccount.AccountNumber,
        render: () => <div>{bankAccount.accountNumber}</div>,
        hidden: !bankAccount.format.displayAccountNumberInput,
        isEmpty: !bankAccount.accountNumber,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
            focusOnMount
            maxLength={serverReference.constants.bankAccount.maxAccountNumberLength}
            value={value}
            onChange={(nr) => { onChange(nr); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.accountNumber,
          submitChange: (accountNumber: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            accountNumber,
          }),
        },
      }, {
        key: "transitNumber",
        label: RESX.BankAccount.TransitNumber,
        render: () => <div>{bankAccount.transitNumber}</div>,
        hidden: !bankAccount.format.displayTransitNumberInput,
        isEmpty: !bankAccount.transitNumber,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <TransitNumberInput
            focusOnMount
            validatorKey="transitNumber"
            value={value}
            onChanged={(nr) => { onChange(nr); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.transitNumber,
          submitChange: (transitNumber: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            transitNumber,
          }),
        }
      }, {
        key: "bsbCode",
        label: RESX.BankAccount.BsbCode,
        render: () => <div>{bankAccount.bsbCode}</div>,
        hidden: !bankAccount.format.displayBsbCodeInput,
        isEmpty: !bankAccount.bsbCode,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <BsbCodeInput
            focusOnMount
            value={value}
            onChanged={value => onChange(value)}
            onEnter={() => submit()}
            onEsc={() => cancel()}
            validatorKey="bsbCode"
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.bsbCode,
          submitChange: (bsbCode: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            bsbCode,
          }),
        },
      }, {
        key: "ibanNumber",
        label: RESX.BankAccount.IbanNumber,
        render: () => <div>{bankAccount.iban}</div>,
        hidden: !bankAccount.format.displayIbanNumberInput,
        isEmpty: !bankAccount.iban,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <IbanInput
            focusOnMount
            value={value}
            onChanged={(v) => onChange(v)}
            onEnter={() => submit()}
            onEsc={() => cancel()}
            validatorKey="iban"
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.iban ?? "",
          submitChange: (ibanNumber: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            ibanNumber,
          }),
        },
      }, {
        key: "sortCode",
        label: RESX.BankAccount.SortCode,
        render: () => <div>{bankAccount.sortCode}</div>,
        hidden: !bankAccount.format.displaySortCodeInput,
        isEmpty: !bankAccount.sortCode,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <SortCodeInput
            focusOnMount
            value={value}
            onChanged={(v) => onChange(v)}
            onEnter={() => submit()}
            onEsc={() => cancel()}
            validatorKey="iban"
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.sortCode,
          submitChange: (sortCode: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            sortCode,
          }),
        },
      }, {
        key: "swiftCode",
        label: RESX.BankAccount.SwiftCode,
        render: () => <div>{bankAccount.swiftCode}</div>,
        hidden: !bankAccount.format.displaySwiftCodeInput,
        isEmpty: !bankAccount.swiftCode,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <SwiftCodeInput
            focusOnMount
            value={value}
            onChanged={(code) => { onChange(code); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
            validatorKey="swiftCode"
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.swiftCode,
          submitChange: (swiftCode: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            swiftCode,
          }),
        },
      }, {
        key: "bankAddress",
        label: RESX.BankAccount.Bank,
        render: () => <div>{bankAccount.bankAddress}</div>,
        isEmpty: !bankAccount.bankAddress,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
            focusOnMount
            maxLength={serverReference.constants.bankAccount.maxBankAddressLength}
            placeholder={RESX.BankAccount.BankPlaceholder}
            value={value}
            onChange={(address) => { onChange(address); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.bankAddress,
          submitChange: (bankAddress: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            bankAddress,
          }),
        }
      }, {
        key: "branchName",
        label: RESX.BankAccount.BranchName,
        render: () => <div>{bankAccount.branchName}</div>,
        hidden: !bankAccount.format.displayBranchNameInput,
        isEmpty: !bankAccount.branchName,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
            focusOnMount
            maxLength={serverReference.constants.bankAccount.maxBranchNameLength}
            value={value}
            onChange={(name) => { onChange(name); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.branchName,
          submitChange: (branchName: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            branchName,
          }),
        }
      }, {
        key: "branchCode",
        label: RESX.BankAccount.BranchCode,
        render: () => <div>{bankAccount.branchCode}</div>,
        hidden: !bankAccount.format.displayBranchCodeInput,
        isEmpty: !bankAccount.branchCode,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
            focusOnMount
            maxLength={serverReference.constants.bankAccount.maxBranchCodeLength}
            value={value}
            onChange={(code) => { onChange(code); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.branchCode,
          submitChange: (branchCode: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            branchCode,
          }),
        }
      }, {
        key: "memo",
        label: RESX.BankAccount.Memo,
        render: () => <div>{bankAccount.memo}</div>,
        isEmpty: !bankAccount.memo,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
            focusOnMount
            maxLength={serverReference.constants.bankAccount.maxMemoLength}
            value={value}
            onChange={(memo) => { onChange(memo); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bankAccount.memo ?? "",
          submitChange: (memo: string) => window.CoreApi.BankAccount.UpdateBankAccount({
            ...getUpdateBankAccountInstruction(bankAccount),
            memo,
          }),
        }
      }]
    }, {
      key: "bookkeepings",
      type: "custom",
      render: () => <LinkedBookkeepingsBankAccountPanel
        key="bookkeepings"
        bankAccountId={props.bankAccount.bankAccountId}
      />
    }]}
  />
}